import React, { useContext } from 'react';
import classnames from 'classnames';
import { PapiUser } from '@wix/da-papi-types';
import {
  Url,
  USER_PROFILE_POSTS_SUB_PATHS,
  USER_PROFILE_SHOP_SUB_PATHS,
  USER_PROFILE_SECTION_PATHS,
} from '@wix/da-url';
import {
  BiData,
  BiContextProvidedValue,
  UserNameClickBiEvent,
} from '@wix/da-bi/pkg/events';
import BiLink, {
  type Props as BiLinkProps,
} from '@wix/da-bi/pkg/Components/BiLink';
import { ResourceType } from '../../../types/resource';
import { BiContext } from '@wix/da-bi/pkg/BiLogger.context';
import { useRestrictToLoggedIn } from '../../../utils/hooks/useRestrictToLoggedIn';
import { isGroup } from '../../helpers';
import s from './BasicUserLink.scss';

function getLinkUrl(
  { username }: PapiUser,
  linkTo?: 'home' | 'gallery' | 'favourites' | 'posts' | 'shop' | 'about',
  anchor?: boolean,
  deviationIdSeed?: number
) {
  switch (linkTo) {
    case 'gallery':
      return Url.userGalleryLink({
        username,
        gallectionType: 'gallery',
        anchor,
        deviationIdSeed,
      });
    case 'favourites':
      return Url.userGalleryLink({
        username,
        gallectionType: 'collection',
        anchor,
        deviationIdSeed,
      });
    case 'posts':
      return Url.userPostsLink({
        username,
        subPath: USER_PROFILE_POSTS_SUB_PATHS.ALL,
        anchor,
      });
    case 'shop':
      return Url.userShopPath(
        username,
        USER_PROFILE_SHOP_SUB_PATHS.ALL,
        anchor
      );
    case 'about':
      return Url.userLink(username, USER_PROFILE_SECTION_PATHS.ABOUT, anchor);
    case 'home':
    default:
      return Url.userLink(username, '', anchor);
  }
}

export interface Props extends Omit<BiLinkProps, 'biData'> {
  user: PapiUser;
  linkTo?: 'home' | 'gallery' | 'favourites' | 'posts' | 'shop' | 'about';
  anchor?: boolean;
  deviationIdSeed?: number;
  biData?: Partial<Omit<UserNameClickBiEvent, 'sectionname'>>;
  children?: React.ReactNode;
}

export const BasicUserLink: React.FC<Props> = ({
  user,
  user: { type, usericon, username, userId, useridUuid },
  linkTo,
  anchor,
  deviationIdSeed,
  children,
  className,
  biData,
  ...rest
}) => {
  const { isLoggedIn } = useRestrictToLoggedIn();
  const biContext = useContext(BiContext);

  if (
    !isLoggedIn &&
    biContext.component === 'art' &&
    biContext.view === 'submission' &&
    biContext.sectionname !== 'author_popup'
  ) {
    linkTo = 'gallery';
  }

  const userType = type === 'super group' ? 'super' : type;

  const biEventData = BiData<UserNameClickBiEvent>({
    itemid: user.userId,
    typeid: isGroup(user) ? ResourceType.GROUP : ResourceType.USER,
    ...biData,
    evid: 101,
    sectionname: BiContextProvidedValue,
  });

  return (
    <BiLink
      biData={biEventData}
      data-username={username}
      data-icon={usericon}
      data-usersymbol={type}
      data-isgroup={isGroup(user) ? '1' : '0'}
      data-userid={userId}
      data-useruuid={useridUuid}
      href={getLinkUrl(user, linkTo, anchor, deviationIdSeed)}
      className={classnames(
        'user-link',
        s['user-link'],
        s[`link-${userType}`],
        className
      )}
      {...rest}
    >
      {children}
    </BiLink>
  );
};
BasicUserLink.displayName = 'BasicUserLink';

export default BasicUserLink;
